import { AnalyticsContext } from "@/Contexts/AnalyticsContext";
import { PropsWithChildren, useContext, useEffect, useRef } from "react";

type VisibilityProps = PropsWithChildren<{
    className?: string;
    fractionVisibleToTrigger?: number;
    onVisible: () => void;
}>;

export default function Visibility({
    className,
    onVisible,
    fractionVisibleToTrigger = 0.5,
    children,
}: VisibilityProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    onVisible();
                }
            },
            {
                threshold: fractionVisibleToTrigger,
            },
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, []);

    return (
        <div ref={containerRef} className={className}>
            {children}
        </div>
    );
}

type ScrolledIntoViewTrackerProps = PropsWithChildren<{
    eventName: string;
}>;

export function ScrolledIntoViewTracker({
    children,
    eventName,
}: ScrolledIntoViewTrackerProps) {
    const { trackEvent } = useContext(AnalyticsContext);

    return (
        <Visibility
            className="w-full"
            onVisible={() => trackEvent(eventName)}
            fractionVisibleToTrigger={0.25}
        >
            {children}
        </Visibility>
    );
}
